import {
  applySnapshot,
  flow,
  getSnapshot,
  Instance,
  SnapshotOut,
  types,
} from "mobx-state-tree";
import {
  createCreateEmailTemplateStore,
  CreateEmailTemplateStore,
} from "./CreateEmailTemplateStore";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { GetEmailTemplateDetailsRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getEmailTemplateDetailsRPC";
import { getAPIClient } from "../../networking/APIClient";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";
import { useGetEmailTemplateDetailsRPCClientImpl } from "../rpcs/RPC";
import {
  BoundedNameModel,
  createBoundedNameModel,
} from "../../models/BoundedNameModel";
import {
  createEmailTemplateContentModel,
  EmailTemplateContentModel,
} from "../models/EmailTemplateContentModel";
import {
  createUpdateEmailTemplateStore,
  UpdateEmailTemplateStore,
} from "./UpdateEmailTemplateStore";
import {
  createDeleteEmailTemplateStore,
  DeleteEmailTemplateStore,
} from "./DeleteEmailTemplateStore";

const getInitialState = (): SnapshotOut<typeof EmailTemplateDetailsStore> => ({
  emailTemplateName: createBoundedNameModel(),
  emailTemplateContent: createEmailTemplateContentModel(),
  loadingEmailTemplateDetails: false,
  getEmailTemplateDetailsRPCError: null,
  createEmailTemplateStore: createCreateEmailTemplateStore(),
  updateEmailTemplateStore: createUpdateEmailTemplateStore(),
  deleteEmailTemplateStore: createDeleteEmailTemplateStore(),
});

export const EmailTemplateDetailsStore = types
  .model("EmailTemplateDetailsStore", {
    emailTemplateName: BoundedNameModel,
    emailTemplateContent: EmailTemplateContentModel,
    loadingEmailTemplateDetails: types.boolean,
    getEmailTemplateDetailsRPCError: types.maybeNull(
      types.enumeration(
        "GetEmailTemplateDetailsRPCError",
        Object.values(GetEmailTemplateDetailsRPC.RPCError),
      ),
    ),
    createEmailTemplateStore: CreateEmailTemplateStore,
    updateEmailTemplateStore: UpdateEmailTemplateStore,
    deleteEmailTemplateStore: DeleteEmailTemplateStore,
  })
  .actions((store) => ({
    getEmailTemplateDetails: flow(function* (
      templateId: LeoUUID,
      syncUpdateStore = false,
    ) {
      try {
        store.loadingEmailTemplateDetails = true;
        store.getEmailTemplateDetailsRPCError = null;

        const apiClient = getAPIClient(store);
        const request = new GetEmailTemplateDetailsRPC.Request(templateId);

        const result: LeoRPCResult<
          GetEmailTemplateDetailsRPC.Response,
          GetEmailTemplateDetailsRPC.Errors.Errors
        > =
          yield useGetEmailTemplateDetailsRPCClientImpl(apiClient).execute(
            request,
          );
        if ("response" in result) {
          store.emailTemplateName.setName(result.response.templateName.name);
          store.emailTemplateContent.setContent(
            result.response.templateContent.content,
          );

          if (!syncUpdateStore) return;
          store.updateEmailTemplateStore.templateId = templateId.uuid;
          store.updateEmailTemplateStore.emailTemplateName.setName(
            result.response.templateName.name,
          );
          store.updateEmailTemplateStore.emailTemplateContent.setContent(
            result.response.templateContent.content,
          );
        } else {
          switch (result.error.code) {
            case GetEmailTemplateDetailsRPC.RPCError.InvalidTemplateId:
              store.getEmailTemplateDetailsRPCError = result.error.code;
              break;
            default:
              result.error.code satisfies never;
          }
        }
      } finally {
        store.loadingEmailTemplateDetails = false;
      }
    }),
    resetStore: (): void => {
      const {
        emailTemplateName,
        emailTemplateContent,
        loadingEmailTemplateDetails,
        getEmailTemplateDetailsRPCError,
      } = getInitialState();
      applySnapshot(store, {
        ...getSnapshot(store),
        emailTemplateName,
        emailTemplateContent,
        loadingEmailTemplateDetails,
        getEmailTemplateDetailsRPCError,
      });
    },
  }));

export const createEmailTemplateDetailsStore = (): Instance<
  typeof EmailTemplateDetailsStore
> => EmailTemplateDetailsStore.create(getInitialState());
